import { useGraphQLQuery } from '@betterment/graphql';
import { StepProps, useAutoNavigate } from '@betterment/js-runtime';
import { Banner, Box, H1, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';

import { AdvisorNetworkIntentStatus } from '../../graphql/schema';
import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { urlBuilder } from '../../Shared/urlBuilder';
import { FinancialServiceOptionCard } from '../components/FinancialServiceOptionCard';
import { TransferSupport } from '../components/TransferSupport';
import { FinancialServiceOptionsDocument } from '../graphql/query.financialServiceOptions.generated';

export const PlanSelectionStep: React.FC<StepProps> = ({ flowState, goToNextStep }) => {
  const navigate = useAutoNavigate();

  const { data } = useGraphQLQuery(FinancialServiceOptionsDocument, {
    throwOnError: true,
  });

  if (!data?.financialServiceOptions.length) return null;

  const { financialServiceOptions, user } = data;

  const intentStatusInfo =
    user.advisorNetworkIntent?.status === AdvisorNetworkIntentStatus.Interested
      ? 'Your advisor match call is booked. Check your email to view or update it.'
      : 'Your advisor matches are on the way. You’ll get an email soon to review them and book your intro calls.';

  return (
    <WorkflowLayout
      feature="advisor_network"
      pageTitle="Plan Selection"
      workflowTitle=""
      onClose={() => navigate(flowState.returnToPath)}
      size="full"
    >
      <Box css={{ display: 'grid', gap: 'space.500', placeItems: 'center', maxWidth: '800px', margin: '0 auto' }}>
        <H1 variant="pageTitle">Choose the level of advice that fits your needs.</H1>
        <Box css={{ display: 'grid', gap: 'space.300', placeItems: 'center' }}>
          {user.advisorNetworkIntent && (
            <Banner variant="info" css={{ width: '100%' }} data-testid="banStatusBanner">
              {intentStatusInfo}
            </Banner>
          )}

          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: {
                base: '1fr 1fr',
                mdDown: '1fr',
              },
              gap: 'space.200',
              width: '100%',
            }}
          >
            {financialServiceOptions.map(option => (
              <FinancialServiceOptionCard
                key={option.id}
                financialServiceOption={option}
                isBAN={option.__typename === 'BettermentAdvisorNetworkOption'}
                goToNextStep={goToNextStep}
              />
            ))}
          </Box>
          <Text>
            Not sure which is right for you?{' '}
            <SambaAutoLink to={urlBuilder.appAdviceInitiationInterests.absolute()}>Schedule a call</SambaAutoLink> with
            our team to learn more about our advisors.
          </Text>
        </Box>
        <TransferSupport />
      </Box>
    </WorkflowLayout>
  );
};
